import { useQuery } from '@tanstack/react-query';
import { Copy, Download, Search, WrapText } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/States';
import { ru } from '@/i18n/ru';
import { cn } from '@/lib/cn';
import { libraryApi } from '@/lib/endpoints';
import { downloadUrl, formatSizeSafe } from '@/lib/format';
import { ApiClientError } from '@/lib/api';

interface TextViewerProps {
  fileId: string | null;
  filename?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TextViewer({ fileId, filename, open, onOpenChange }: TextViewerProps) {
  const [wrap, setWrap] = useState(true);
  const [find, setFind] = useState('');
  const [findIndex, setFindIndex] = useState(0);
  const activeLineRef = useRef<HTMLDivElement | null>(null);

  const textQuery = useQuery({
    queryKey: ['file-text', fileId],
    queryFn: () => libraryApi.text(fileId!),
    enabled: open && Boolean(fileId),
    staleTime: 60_000,
  });

  const lines = useMemo(() => {
    const text = textQuery.data?.text ?? '';
    return text.split('\n');
  }, [textQuery.data?.text]);

  const matchLineIndexes = useMemo(() => {
    const q = find.trim().toLowerCase();
    if (!q) return [] as number[];
    const hits: number[] = [];
    lines.forEach((line, i) => {
      if (line.toLowerCase().includes(q)) hits.push(i);
    });
    return hits;
  }, [lines, find]);

  const activeMatchLine = matchLineIndexes[findIndex] ?? matchLineIndexes[0];

  useEffect(() => {
    if (activeMatchLine == null) return;
    activeLineRef.current?.scrollIntoView({ block: 'center' });
  }, [activeMatchLine, findIndex]);

  const copyAll = async () => {
    if (!textQuery.data?.text) return;
    try {
      await navigator.clipboard.writeText(textQuery.data.text);
      toast.success(ru.copied);
    } catch {
      toast.error(ru.copyFailed);
    }
  };

  const jumpNext = () => {
    if (!matchLineIndexes.length) return;
    setFindIndex((i) => (i + 1) % matchLineIndexes.length);
  };

  const errMsg =
    textQuery.error instanceof ApiClientError
      ? textQuery.error.message
      : textQuery.error
        ? ru.failedLoadText
        : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={filename ?? textQuery.data?.filename ?? ru.textFile}
        description={
          textQuery.data
            ? `${textQuery.data.encoding} · ${formatSizeSafe(textQuery.data.byteLength)}`
            : undefined
        }
        data-testid="text-viewer"
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-3 py-2">
            <div className="relative min-w-[180px] flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
              <Input
                value={find}
                onChange={(e) => {
                  setFind(e.target.value);
                  setFindIndex(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    jumpNext();
                  }
                }}
                placeholder={ru.findInFile}
                className="pl-7"
                data-testid="text-find"
              />
            </div>
            {find.trim() ? (
              <span className="font-mono text-[10px] text-text-muted tabular-nums">
                {matchLineIndexes.length
                  ? `${Math.min(findIndex + 1, matchLineIndexes.length)}/${matchLineIndexes.length}`
                  : '0'}
              </span>
            ) : null}
            <Button size="sm" variant="ghost" onClick={jumpNext} disabled={!matchLineIndexes.length}>
              {ru.next}
            </Button>
            <Button
              size="sm"
              variant={wrap ? 'outline' : 'ghost'}
              onClick={() => setWrap((w) => !w)}
              aria-pressed={wrap}
            >
              <WrapText className="h-3.5 w-3.5" />
              {ru.wrap}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void copyAll()} disabled={!textQuery.data}>
              <Copy className="h-3.5 w-3.5" />
              {ru.copy}
            </Button>
            {fileId ? (
              <a
                href={downloadUrl(fileId)}
                download
                className="inline-flex h-7 items-center gap-1.5 px-2 text-xs text-text-muted hover:text-text hover:bg-row-hover"
              >
                <Download className="h-3.5 w-3.5" />
                {ru.download}
              </a>
            ) : null}
          </div>

          {textQuery.data?.truncated ? (
            <div className="shrink-0 border-b border-border bg-accent-dim px-3 py-1.5 text-[11px] text-accent">
              {ru.fileExceedsPreview(formatSizeSafe(textQuery.data.maxBytes))}
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-auto bg-bg font-mono text-[12px] leading-5">
            {textQuery.isLoading ? (
              <div className="space-y-1 p-3">
                {Array.from({ length: 20 }).map((_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            ) : null}
            {errMsg ? (
              <ErrorState
                title={ru.cannotOpenFile}
                message={errMsg}
                onRetry={() => void textQuery.refetch()}
              />
            ) : null}
            {textQuery.data ? (
              <pre
                className={cn('m-0 p-0', wrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre')}
              >
                {lines.map((line, i) => {
                  const isHit = activeMatchLine === i;
                  return (
                    <div
                      key={i}
                      ref={isHit ? activeLineRef : undefined}
                      className={cn('flex border-b border-border/20', isHit && 'bg-accent-dim')}
                    >
                      <span className="sticky left-0 w-12 shrink-0 select-none border-r border-border/40 bg-surface/80 px-2 text-right text-text-muted tabular-nums">
                        {i + 1}
                      </span>
                      <span className="flex-1 px-3 text-text">{line.length ? line : ' '}</span>
                    </div>
                  );
                })}
              </pre>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
